/**
 * AI Content Generation API
 *
 * Frontend helper for calling the AI content generation endpoint.
 * Uses the Anthropic Claude API via the Supabase edge function.
 *
 * SECURITY: All requests include session token for server-side auth.
 */

import { apiFetch } from "./apiClient";

export interface AiGenerateParams {
  /** HTML element type: h1, h2, h3, p, span, li, etc. */
  elementType: string;
  /** Content key, e.g. "hero:h1", "about:paragraph:1" */
  contentKey: string;
  /** Current text content of the element */
  currentContent: string;
  /** Business/client name */
  businessName?: string;
  /** Business type / industry (e.g. "\u05E0\u05D3\u05DC\"\u05DF", "\u05DE\u05E1\u05E2\u05D3\u05D4") */
  businessType?: string;
  /** Additional page context */
  pageContext?: string;
  /** Optional user instructions (e.g. "\u05D9\u05D5\u05EA\u05E8 \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9", "\u05E7\u05E6\u05E8 \u05D9\u05D5\u05EA\u05E8") */
  userPrompt?: string;
}

export interface AiGenerateResult {
  content: string;
}

/**
 * Generate AI content for a specific element.
 * Returns the generated text string.
 */
export async function generateAiContent(
  params: AiGenerateParams
): Promise<string> {
  const data: AiGenerateResult = await apiFetch("/ai-generate", {
    method: "POST",
    body: JSON.stringify(params),
  });
  return data.content;
}

// ======== AI SEO Optimizer API ========

export interface SeoOptimizeParams {
  /** Page ID from seoData */
  pageId: string;
  /** Human-readable page name */
  pageName: string;
  /** Current SEO title */
  currentTitle: string;
  /** Current meta description */
  currentDescription: string;
  /** Business name */
  businessName: string;
  /** Business type / industry */
  businessType: string;
  /** Template type: branding / store */
  pageType: string;
  /** Optional user instructions */
  userInstructions?: string;
}

export interface SeoOptimizeResult {
  title: string;
  description: string;
}

/**
 * Optimize SEO title + description for a single page.
 */
export async function optimizeSeo(
  params: SeoOptimizeParams
): Promise<SeoOptimizeResult> {
  return apiFetch("/ai-seo-optimize", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface BulkSeoPage {
  pageId: string;
  pageName: string;
  currentTitle: string;
  currentDescription: string;
  pageType: string;
}

export interface BulkSeoResult {
  pageId: string;
  title: string;
  description: string;
}

/**
 * Optimize SEO for multiple pages in a single API call.
 */
export async function optimizeSeoBulk(params: {
  pages: BulkSeoPage[];
  businessName: string;
  businessType: string;
}): Promise<BulkSeoResult[]> {
  const data = await apiFetch("/ai-seo-optimize-bulk", {
    method: "POST",
    body: JSON.stringify(params),
  });
  return data.results;
}
